import { EVENTS } from './constants';
import {
  formatProduct,
  formatProducts,
  formatCart,
  formatMiniCartItem
} from './formatters';

/**
 * Create view_item_list event
 * Triggered when a user views a list of items
 *
 * @param {Object} params
 * @param {Array} params.products - Array of products
 * @param {string} params.listName - Name of the list
 * @param {string} params.listId - ID of the list (optional)
 * @param {string} params.currency - Currency code
 * @param {Array} params.routeParents - Route parents for category hierarchy (optional)
 * @returns {Object} GA4 event data
 */
export function createViewItemListEvent({
  products,
  listName,
  listId,
  currency,
  routeParents
}) {
  const items = formatProducts(products, { listName, routeParents });

  return {
    event: EVENTS.VIEW_ITEM_LIST,
    ecommerce: {
      item_list_name: listName,
      item_list_id: listId || listName?.toLowerCase().replace(/\s+/g, '_'),
      list_length: items.length,
      currency: currency,
      items: items
    }
  };
}

/**
 * Create select_item event
 * Triggered when a user selects an item from a list
 *
 * @param {Object} params
 * @param {Object} params.product - Product object
 * @param {string} params.listName - Name of the list
 * @param {number} params.index - Position in list
 * @param {string} params.currency - Currency code
 * @returns {Object} GA4 event data
 */
export function createSelectItemEvent({
  product,
  listName,
  index,
  currency
}) {
  const item = formatProduct(product, { listName, index });

  return {
    event: EVENTS.SELECT_ITEM,
    ecommerce: {
      item_list_name: listName,
      item_list_id: listName?.toLowerCase().replace(/\s+/g, '_'),
      currency: currency,
      items: [item]
    }
  };
}

/**
 * Create view_item event
 * Triggered when a user views an item
 *
 * @param {Object} params
 * @param {Object} params.product - Product object
 * @param {Object} params.variant - Selected variant (optional)
 * @param {string} params.currency - Currency code
 * @returns {Object} GA4 event data
 */
export function createViewItemEvent({ product, variant, currency }) {
  const item = formatProduct(product, { variant });

  return {
    event: EVENTS.VIEW_ITEM,
    ecommerce: {
      currency: currency,
      value: item.price,
      items: [item]
    }
  };
}

/**
 * Create add_to_cart event
 * Triggered when a user adds items to their shopping cart
 *
 * @param {Object} params
 * @param {Object} params.product - Product object
 * @param {Object} params.variant - Selected variant (optional)
 * @param {number} params.quantity - Quantity added
 * @param {string} params.currency - Currency code
 * @param {string} params.placement - Where the add to cart happened (e.g., "Product page", "Favorites")
 * @returns {Object} GA4 event data
 */
export function createAddToCartEvent({
  product,
  variant,
  quantity = 1,
  currency,
  placement
}) {
  const item = formatProduct(product, { variant, quantity });

  return {
    event: EVENTS.ADD_TO_CART,
    ecommerce: {
      currency: currency,
      value: item.price * item.quantity,
      placement: placement,
      items: [item]
    }
  };
}

/**
 * Create remove_from_cart event
 * Triggered when a user removes items from their shopping cart
 *
 * @param {Object} params
 * @param {Object} params.product - Product object OR cart item
 * @param {Object} params.variant - Selected variant (optional)
 * @param {number} params.quantity - Quantity removed
 * @param {string} params.currency - Currency code
 * @param {string} params.placement - Where the removal happened (e.g., "Mini cart")
 * @returns {Object} GA4 event data
 */
export function createRemoveFromCartEvent({
  product,
  variant,
  quantity = 1,
  currency,
  placement
}) {
  const item = formatProduct(product, { variant, quantity });

  return {
    event: EVENTS.REMOVE_FROM_CART,
    ecommerce: {
      currency: currency,
      value: item.price * item.quantity,
      placement: placement,
      items: [item]
    }
  };
}

/**
 * Create view_cart event
 * Triggered when a user views their shopping cart
 *
 * @param {Object} params
 * @param {Object} params.cart - Cart object
 * @returns {Object} GA4 event data
 */
export function createViewCartEvent({ cart }) {
  const formattedCart = formatCart(cart);

  return {
    event: EVENTS.VIEW_CART,
    ecommerce: formattedCart
  };
}

/**
 * Create view_mini_cart event
 * Triggered when a user views the mini cart (cart flyout/drawer)
 *
 * @param {Object} params
 * @param {Object} params.cart - Cart object
 * @returns {Object} GA4 event data
 */
export function createViewMiniCartEvent({ cart }) {
  if (!cart || !cart.items) return null;

  const currency = cart.currency?.code || 'SEK';
  const value = cart.productTotal?.incVat || 0;
  
  // Format all cart items with extended fields
  const items = cart.items.map((item, index) => 
    formatMiniCartItem(item, index, currency)
  ).filter(Boolean);

  return {
    event: 'view_mini_cart',
    ecommerce: {
      currency: currency,
      value: value,
      items: items
    }
  };
}

/**
 * Create begin_checkout event
 * Triggered when a user begins checkout
 *
 * @param {Object} params
 * @param {Object} params.cart - Cart object
 * @returns {Object} GA4 event data
 */
export function createBeginCheckoutEvent({ cart }) {
  const formattedCart = formatCart(cart);

  return {
    event: EVENTS.BEGIN_CHECKOUT,
    ecommerce: formattedCart
  };
}

/**
 * Create purchase event
 * Triggered when a user completes a purchase
 *
 * @param {Object} params
 * @param {Object} params.order - Order object
 * @param {string} params.transactionId - Transaction/Order ID
 * @returns {Object} GA4 event data
 */
export function createPurchaseEvent({ order, transactionId }) {
  const items = order.items?.map(item => ({
    item_id: item.articleNumber,
    item_name: item.product?.name || item.articleNumber,
    price: item.unitPrice?.incVat || 0,
    quantity: item.quantity
  }));

  return {
    event: EVENTS.PURCHASE,
    ecommerce: {
      transaction_id: transactionId || order.id,
      currency: order.currency?.code || 'SEK',
      value: order.total?.incVat || 0,
      tax: order.total?.vat || 0,
      shipping: order.shippingCost?.incVat || 0,
      items: items
    }
  };
}

/**
 * Create view_promotion event
 * Triggered when a user views a promotion (banner)
 *
 * @param {Object} params
 * @param {string} params.itemId - Promotion/banner ID
 * @param {string} params.itemName - Promotion/banner name
 * @param {string} params.creativeSlot - Where the promotion appears (e.g., "Front page", "Hero")
 * @returns {Object} GA4 event data
 */
export function createViewPromotionEvent({ itemId, itemName, creativeSlot }) {
  return {
    event: EVENTS.VIEW_PROMOTION,
    ecommerce: {
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          creative_slot: creativeSlot
        }
      ]
    }
  };
}

/**
 * Create select_promotion event
 * Triggered when a user clicks/selects a promotion (banner)
 *
 * @param {Object} params
 * @param {string} params.itemId - Promotion/banner ID
 * @param {string} params.itemName - Promotion/banner name
 * @param {string} params.creativeSlot - Where the promotion appears (e.g., "Front page", "Hero")
 * @returns {Object} GA4 event data
 */
export function createSelectPromotionEvent({ itemId, itemName, creativeSlot }) {
  return {
    event: EVENTS.SELECT_PROMOTION,
    ecommerce: {
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          creative_slot: creativeSlot
        }
      ]
    }
  };
}

/**
 * Create popular_category_click event
 * Triggered when user clicks on a popular category link
 *
 * @param {Object} params
 * @param {string} params.clickName - Name of the category clicked
 * @param {number} params.index - Position/index of the category
 * @returns {Object} GA4 event data
 */
export function createPopularCategoryClickEvent({ clickName, index }) {
  return {
    event: 'popular_category_click',
    click_name: clickName,
    index: index
  };
}

/**
 * Create search event
 * Triggered when the user uses the search function
 *
 * @param {Object} params
 * @param {string} params.searchTerm - Search term
 * @param {number} params.results - Number of results
 * @param {string} params.placement - "Quick search" or "Deep search"
 * @returns {Object} GA4 event data
 */
export function createSearchEvent({ searchTerm, results, placement }) {
  return {
    event: EVENTS.SEARCH,
    search: {
      search_term: searchTerm,
      results: results || 0,
      placement: placement || 'Deep search'
    }
  };
}

/**
 * Create page_view event
 * Triggered on every page a user navigates to
 *
 * @param {Object} params
 * @param {string} params.pageType - Type of page
 * @param {string} params.pagePath - Page path
 * @param {string} params.pageTitle - Page title
 * @returns {Object} GA4 event data
 */
export function createPageViewEvent({ pageType, pagePath, pageTitle, type }) {
  return {
    event: EVENTS.PAGE_VIEW,
    page_data: {
      page_type: pageType,
      page_path: pagePath,
      page_title: pageTitle,
      type: type,
      newTracking: true
    }
  };
}

/**
 * Create search_result_click event
 * Triggered when a user clicks on a search result
 *
 * @param {Object} params
 * @param {string} params.searchTerm - The search term
 * @param {number} params.results - Total number of results
 * @param {string} params.clickedTitle - Title of clicked item
 * @param {string} params.clickedUrl - URL of clicked item
 * @param {number} params.position - Position in results (1-based)
 * @param {string} params.placement - "Quick search" or "Deep search"
 * @returns {Object} GA4 event data
 */
export function createSearchResultClickEvent({ 
  searchTerm, 
  results,
  clickedTitle, 
  clickedUrl, 
  position, 
  placement 
}) {
  return {
    event: 'search_result_click',
    search: {
      search_term: searchTerm,
      results: results || 0,
      clicked_title: clickedTitle,
      clicked_url: clickedUrl,
      position: position,
      placement: placement || 'Deep search'
    }
  };
}

